import React, { useReducer } from "react";

import { css } from "@emotion/css";
import { QueryAwait } from "@steto-care/steto-query";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { RenewType } from "@/models/PatientRenewalInfoModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { CenteredLoader } from "@/uiKit/atoms/Loader.tsx";
import { SideSheet } from "@/uiKit/atoms/SideSheet.tsx";
import { HandledErrorCard } from "@/uiKit/organisms/HandledErrorCard.tsx";
import { CarePlanRenewalForm } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalForm.tsx";
import { CarePlanRenewalFormActions } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalFormActions.tsx";
import { CarePlanRenewalFormContent } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalFormContent.tsx";
import {
  useGeneratePrescription,
  useRenewPatient,
  useSignPrescriptionAndRenewPatient,
} from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationAndRenewalActions.ts";
import { RenewalProvider } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewalProvider.tsx";
import { patientRenewalWizard } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewalWizard.ts";
import { PrescriptionInfoForm } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoForm.tsx";
import { PrescriptionInfoFormActions } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormActions.tsx";
import { PrescriptionInfoFormContent } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormContent.tsx";
import { PrescriptionInfoFormTitle } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormTitle.tsx";
import { PrescriptionSignActions } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignActions.tsx";
import { PrescriptionSignContent } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignContent.tsx";

export type PatientRenewalProps = {
  patientId: string;
  open: boolean;
  renewalType: RenewType;
  onClose: () => void;
  onSubmit: () => void;
};

export const PatientRenewal: React.FC<PatientRenewalProps> = props => {
  return (
    <SideSheet id="patient-renewal" open={props.open} onClose={props.onClose}>
      <QueryAwait errorFallback={HandledErrorCard} pendingFallback={CenteredLoader}>
        <CarePlanRenewalContent {...props} />
      </QueryAwait>
    </SideSheet>
  );
};

const CarePlanRenewalContent: React.FC<PatientRenewalProps> = ({ patientId, onSubmit, onClose, renewalType }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const carePlanDirectives = Queries.practitioner.useCarePlanDirectives({
    patientId: patientId,
    renewalType,
  });

  const [state, dispatch] = useReducer(patientRenewalWizard.reducer, patientRenewalWizard.initialState);
  const generatePrescription = useGeneratePrescription();
  const patient = Queries.practitioner.usePatient(patientId);
  const { callback: renewPatient } = useRenewPatient(onSubmit);
  const { submit: signAndRenewPatient, isPending: isSignAndRenewPending } =
    useSignPrescriptionAndRenewPatient(onSubmit);

  const previousConditionData = carePlanDirectives.previousConditionData;

  return (
    <RenewalProvider carePlanDirectives={carePlanDirectives} renewalType={renewalType}>
      <CardHeader
        id="patient-renewal-header"
        title={renewalType === "dg_start_insulin" ? t("diabetesForms.addInsulin") : t("diabetesForms.renew")}
        variant="contained"
      />

      <CarePlanRenewalForm
        carePlanInfo={
          state.CarePlan ?? {
            diabetesType: previousConditionData?.diabetesType,
            insulinScheme: previousConditionData?.insulinType,
            termDate: previousConditionData?.termDate ?? "",
            inclusionCriteria: carePlanDirectives.previousCarePlanData?.telemonitoringCriteria,
            description: carePlanDirectives.previousCarePlanData?.description,
            descriptionAttachment: carePlanDirectives.previousCarePlanData?.descriptionAttachment,
            treatmentPrescriptionAttachment: carePlanDirectives.previousCarePlanData?.treatmentPrescriptionAttachment,
            referralLetterAttachment: carePlanDirectives.previousCarePlanData?.referralLetterAttachment,
            pump: carePlanDirectives.previousConditionData?.pumpModel,
          }
        }
        onSubmit={data => dispatch({ action: "TO_PrescriptionInfo", data })}
        className={styles.form}
      >
        <CarePlanRenewalFormContent patient={patient} />
        <CarePlanRenewalFormActions
          onBack={data => {
            dispatch({ action: "SET_DATA_CarePlan", data });
            onClose();
          }}
          className={styles.footer}
        />
      </CarePlanRenewalForm>
      <Dialog
        id="prescription-dialog"
        open={state.step === "PrescriptionInfo" || state.step === "PrescriptionSign"}
        onClose={() => {
          if (state.step === "PrescriptionSign") {
            dispatch({ action: "BACK_TO_PrescriptionInfo" });
          }
          dispatch({ action: "BACK_TO_CarePlan" });
        }}
        maxWidth={state.step === "PrescriptionInfo" ? "xs" : "md"}
        classes={{ paper: styles.dialog }}
      >
        {state.step === "PrescriptionInfo" && (
          <PrescriptionInfoForm
            prescriptionInfo={
              state.PrescriptionInfo ?? {
                birthDate: patient.birthDate,
              }
            }
            onGeneratePrescription={data =>
              generatePrescription(
                {
                  patientInfo: patient,
                  carePlan: state.CarePlan,
                  prescriptionInfo: data,
                },
                ({ prescription }) => {
                  dispatch({
                    action: "TO_PrescriptionSign",
                    data: { ...data, prescription },
                  });
                },
              )
            }
            onUploadPrescription={file => {
              renewPatient(patient, state.CarePlan, {}, file);
            }}
          >
            <PrescriptionInfoFormTitle onExit={data => dispatch({ action: "BACK_TO_CarePlan", data })} />
            {state.step === "PrescriptionInfo" && (
              <PrescriptionInfoFormContent
                patientInfo={patient}
                carePlanStartDate={carePlanDirectives.carePlanCreationDataDirectives.periodStart}
              />
            )}
            <PrescriptionInfoFormActions
              onBack={data => dispatch({ action: "BACK_TO_CarePlan", data })}
              submitTitle={t("diabetesForms.renewPatient")}
              className={styles.footer}
            />
          </PrescriptionInfoForm>
        )}
        {state.step === "PrescriptionSign" && (
          <>
            <CardHeader
              id="prescription-sign-header"
              title={t("diabetesForms.prescriptionSignature")}
              variant="contained"
              onExit={() => {
                dispatch({
                  action: "BACK_TO_PrescriptionInfo",
                });
                dispatch({ action: "BACK_TO_CarePlan" });
              }}
            />
            <PrescriptionSignContent prescription={state.PrescriptionInfo.prescription} />
            <PrescriptionSignActions
              submitTitle={t("diabetesForms.signAndRenewPatient")}
              onBack={() => {
                dispatch({
                  action: "BACK_TO_PrescriptionInfo",
                });
              }}
              onSubmit={() => {
                signAndRenewPatient(patient, state.CarePlan, state.PrescriptionInfo);
              }}
              pending={isSignAndRenewPending}
              className={styles.footer}
            />
          </>
        )}
      </Dialog>
    </RenewalProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "hidden",
  }),
  dialog: css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    alignItems: "center",
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
  }),
});
